
import { useEffect, useState, useRef, forwardRef } from "react"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import { ChevronDownIcon, ChevronUpIcon, Info, TrendingUp, IndianRupeeIcon, ArrowRight } from "lucide-react"
// import server from "../../utils/serverConfig";

// import model1 from "../../assests/model1.png"
import appConfig from "../utils/appConfig";

const Card = forwardRef(({ ele, handlePricingCardClick, globalConsent, handleGlobalConsent }, ref) => {
  const { headerRef, statsRef, buttonRef } = ref

  const [isConsentPopupOpen, setIsConsentPopupOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [showMore, setShowMore] = useState(false)
  const [selectedPricing, setSelectedPricing] = useState("monthly")
  const [shouldShowMoreButton, setShouldShowMoreButton] = useState(false)
  const descriptionRef = useRef(null)

  useEffect(() => {
    // Check if description content exceeds the max height
    if (descriptionRef.current) {
      const contentHeight = descriptionRef.current.scrollHeight
      const containerHeight = 150 // Max height before showing "Show More"
      setShouldShowMoreButton(contentHeight > containerHeight)
    }
  }, [ele?.description])

  const handleCAGRClick = (e) => {
    e.stopPropagation()
    if (!globalConsent) {
      setIsConsentPopupOpen(true)
    }
  }

  const handleConsentAccept = () => {
    handleGlobalConsent()
    setIsConsentPopupOpen(false)
  }

  const handleCardClick = () => {
    if (!ele?.subscription) {
      handlePricingCardClick(ele)
    }
  }

  // Calculate months from days
  const calculateMonths = (days) => {
    if (!days) return 12
    return Math.round(days / 30)
  }

  // Get available pricing options
  const getPricingOptions = () => {
    if (ele?.amount) {
      return [
        {
          label: `${calculateMonths(ele.duration)} months`,
          value: ele.amount,
        },
      ]
    }

    const options = []
    if (ele?.pricing?.monthly)
      options.push({
        period: "monthly",
        label: "Monthly",
        value: ele.pricing.monthly,
      })
    if (ele?.pricing?.quarterly)
      options.push({
        period: "quarterly",
        label: "Quarterly",
        value: ele.pricing.quarterly,
      })
    if (ele?.pricing?.["half-yearly"])
      options.push({
        period: "half-yearly",
        label: "6 Months",
        value: ele.pricing["half-yearly"],
      })
    if (ele?.pricing?.yearly)
      options.push({
        period: "yearly",
        label: "Yearly",
        value: ele.pricing.yearly,
      })

    return options
  }

  const pricingOptions = getPricingOptions()
  const currentPrice = ele?.amount || pricingOptions.find((opt) => opt.period === selectedPricing)?.value || 0
  const originalPrice = currentPrice ? Math.round(currentPrice / 0.81) : 0
  const discount =
    originalPrice && currentPrice ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100) : 0
    const descriptionLines = ele?.description
      ?.split("\n")
      .map((point) => point.trim())
      .filter((point) => point.length > 0);
  return (
    <div className="relative w-full max-w-[380px] bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200">
      {/* Portfolio Type Badge */}
      <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-1 rounded-bl-lg text-sm font-medium z-10">
        {ele?.type || "Popular"}
      </div>

      {/* Header with Price */}
      <div
        ref={headerRef}
        className="p-6 cursor-pointer hover:bg-gray-50/50 transition-colors"
        onClick={handleCardClick}
      >
        <div className="flex flex-col my-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">{ele?.name}</h2>
        </div>

        {/* Pricing Options */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-lg p-4 mb-6 border border-blue-200/50">
          {pricingOptions.length > 1 ? (
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                {pricingOptions.map((option) => (
                  <button
                    key={option.period}
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedPricing(option.period)
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedPricing === option.period
                        ? "bg-blue-600 text-white shadow-sm"
                        : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-lg text-gray-500 line-through">₹{originalPrice?.toLocaleString()}</span>
                <span className="text-2xl font-bold text-gray-900">
                  ₹{currentPrice?.toLocaleString()}{" "}
                  {process.env.REACT_APP_AdVISOR_GST_CONFIGURE === "true" ? "+ GST" : ""}
                </span>
                <span className="text-sm font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-100">
                  Save {discount}%
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-baseline gap-2">
              <span className="text-lg text-gray-500 line-through">₹{originalPrice?.toLocaleString()}</span>
              <span className="text-2xl font-bold text-gray-900">
                ₹{currentPrice?.toLocaleString()}{" "}
                {process.env.REACT_APP_AdVISOR_GST_CONFIGURE === "true" ? "+ GST" : ""}
              </span>
              <span className="text-sm font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-100">
                Save {discount}%
              </span>
            </div>
          )}
        </div>

        {/* Stats Section */}
        <div ref={statsRef} className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 mb-2">
              <TrendingUp className="w-4 h-4" />
            </div>
            <div className="text-xs items-center justify-center text-gray-500 font-medium mb-1">CAGR</div>
            <p
              className={`text-sm font-semibold text-center  text-green-600 ${!globalConsent ? "blur-sm" : ""} cursor-pointer`}
              onClick={handleCAGRClick}
            >
              New Portfolio
            </p>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-600 mb-2">
              <TrendingUp className="w-4 h-4" />
            </div>
            <div className="text-xs text-gray-500 font-medium mb-1">2Y Returns</div>
            <p
              className={`text-sm font-semibold text-center text-green-600 ${!globalConsent ? "blur-sm" : ""} cursor-pointer`}
              onClick={handleCAGRClick}
            >
              New Portfolio
            </p>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-600 mb-2">
              <IndianRupeeIcon className="w-4 h-4" />
            </div>
            <div className="text-xs text-center text-gray-500 font-medium mb-1">Min. Investment</div>
            <p className="text-sm font-semibold">₹{ele?.minInvestment?.toLocaleString()}</p>
          </div>
        </div>
        <a
        href="#"
        className="px-3 text-blue-600 text-sm hover:underline flex items-center gap-1 w-fit"
        onClick={(e) => {
          e.stopPropagation()
          // Store the intended destination before navigating
          // sessionStorage.setItem("intendedDestination", `/model-portfolio/${ele?.name}`)
          window.location.href = `/Methodology/${ele?.name}`
        }}
      >
        <Info className="h-3.5 w-3.5" />
        View Portfolio Methodology
      </a>
        {/* Description List */}
        <div className="relative">
          <div
            ref={descriptionRef}
            className={`${
              showMore ? "max-h-[500px]" : "max-h-[150px]"
            } overflow-hidden pr-2 custom-scrollbar transition-all duration-500`}
          >
            <ul className="space-y-3">
              {descriptionLines?.map((point, index) => (
                <li key={index} className="text-gray-700 flex items-start gap-2">
                  <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                  <span
                    className={`block ${
                      !showMore
                        ? "h-[24px] overflow-hidden text-ellipsis whitespace-nowrap"
                        : "min-h-[24px] whitespace-normal break-words"
                    }`}
                  >
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {!showMore && shouldShowMoreButton && (
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
          )}

          {shouldShowMoreButton && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowMore(!showMore)
              }}
              className="mt-3 text-blue-600 text-sm font-medium hover:underline flex items-center"
            >
              {showMore ? "Show Less" : "Show More"}
              {showMore ? <ChevronUpIcon className="ml-1 w-4 h-4" /> : <ChevronDownIcon className="ml-1 w-4 h-4" />}
            </button>
          )}
        </div>

        {/* Expandable Content */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            setIsExpanded(!isExpanded)
          }}
          className={`w-full flex items-center justify-center py-2 text-sm text-gray-600 hover:bg-gray-50 ${
            isExpanded ? "" : "pb-24"
          }`}
        >
          {isExpanded ? (
            <>
              Show Less <ChevronUpIcon className="ml-1 w-4 h-4" />
            </>
          ) : (
            <>
              Show More <ChevronDownIcon className="ml-1 w-4 h-4" />
            </>
          )}
        </button>

        {isExpanded && (
          <div className="mt-4 space-y-6">
            <div>
              <h3 className="text-sm font-semibold mb-3 text-gray-900">Key Features</h3>
              <ul className="space-y-2">
                <li className="text-gray-700 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                  Professionally managed portfolio
                </li>
                <li className="text-gray-700 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                  Regular portfolio rebalancing
                </li>
                <li className="text-gray-700 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                  Optimal stock allocation and monitoring
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-2 text-gray-900">Additional Benefits</h3>
              <p className="text-sm text-gray-700">
                Get alerts via email/WhatsApp and update portfolio in your broker with a mouse click.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Action Button */}
      <div ref={buttonRef} className="w-full absolute bottom-0 p-6 bg-gray-50 border-t border-gray-100">
  <button
    onClick={() => (ele?.subscription ? undefined : handlePricingCardClick(ele))}
    className={`group w-full py-2.5 px-4 rounded-lg font-medium text-sm text-white relative overflow-hidden transition-all duration-300 ${appConfig.colors.button} hover:-translate-y-0.5 shadow-md hover:shadow-blue-300/30 bg-[length:200%_100%] bg-[position:0%_0%] hover:bg-[position:100%_0%]`}
  >
    <span className="relative z-10 flex items-center justify-center">
      Invest Now
      <ArrowRight className="w-4 h-4 ml-1.5 transition-transform duration-300 group-hover:translate-x-1" />
    </span>
  </button>
</div>

      {/* Consent Popup */}
      {isConsentPopupOpen && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 backdrop-blur-sm" />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full border border-gray-200">
              <h2 className="text-xl font-bold mb-4 text-gray-900">Consent Required</h2>
              <p className="mb-6 text-gray-700">
                By clicking to view performance, I understand that historical performance data is NOT a promise of
                future returns. For new portfolio, data would be limited and will not show CAGR.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors text-gray-800"
                  onClick={() => setIsConsentPopupOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  onClick={handleConsentAccept}
                >
                  I Agree
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
})

const PortfolioSection = ({ allPricingPlan = [], handlePricingCardClick }) => {
  const [pricingPlans, setPricingPlans] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [showSkeleton, setShowSkeleton] = useState(true)
  const [globalConsent, setGlobalConsent] = useState(false)
  const cardRefs = useRef([])

  const handleGlobalConsent = () => {
    setGlobalConsent(true)
  }

  useEffect(() => {
    if (allPricingPlan.length > 0) {
      setPricingPlans(allPricingPlan)
      setIsLoading(false)
      setShowSkeleton(false) // Stop showing the skeleton when data is available
    } else {
      const timer = setTimeout(() => {
        setShowSkeleton(false) // Hide skeleton after 3 seconds
        setIsLoading(false)
      }, 3000)

      return () => clearTimeout(timer) // Cleanup timer on component unmount
    }
  }, [allPricingPlan])

  useEffect(() => {
    if (!isLoading) {
      adjustCardHeights()
    }
  }, [isLoading, pricingPlans])

  // Adjust card heights function
  const adjustCardHeights = () => {
    const rows = []
    let currentRow = []
    cardRefs.current.forEach((card, index) => {
      if (card) {
        currentRow.push(card)
        if ((index + 1) % 3 === 0 || index === cardRefs.current.length - 1) {
          rows.push(currentRow)
          currentRow = []
        }
      }
    })

    rows.forEach((row) => {
      const sections = ["headerRef", "statsRef", "infoRef", "buttonRef"]
      sections.forEach((section) => {
        const maxHeight = Math.max(...row.map((card) => card[section]?.offsetHeight || 0))
        row.forEach((card) => {
          if (card[section]) {
            card[section].style.height = `${maxHeight}px`
          }
        })
      })
    })
  }

  const SkeletonCard = () => (
    <div className="w-full max-w-[380px] relative font-poppins min-h-[200px] rounded-xl bg-white border border-gray-100 shadow-md p-6">
      <div className="mb-6">
        <Skeleton width={150} height={28} className="mb-4" />
        <Skeleton width={120} height={20} className="mb-2" />
        <div className="flex gap-2 items-center">
          <Skeleton width={80} height={24} />
          <Skeleton width={100} height={30} />
          <Skeleton width={60} height={24} />
        </div>
      </div>

      <div className="mb-6">
        <Skeleton width="100%" height={80} className="rounded-lg" />
      </div>

      <div className="space-y-3 mb-6">
        <Skeleton width="100%" height={16} />
        <Skeleton width="100%" height={16} />
        <Skeleton width="100%" height={16} />
        <Skeleton width="100%" height={16} />
        <Skeleton width="100%" height={16} />
      </div>

      <div className="mt-8">
        <Skeleton width="100%" height={44} className="rounded-lg" />
      </div>
    </div>
  )

  if (!showSkeleton && pricingPlans?.length === 0) {
    return null // Return null after 3 seconds if no data
  }

  return (
    <div className="py-16 bg-gradient-to-b from-blue-50 to-white" id="modelPortfolio">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 text-center mb-4 sm:mb-10">
          Model Portfolios for Every Investor
        </h1>

        <div className="flex flex-wrap justify-center gap-6 md:gap-8">
          {isLoading
            ? Array(3)
                .fill(0)
                .map((_, i) => <SkeletonCard key={i} />)
            : pricingPlans.map((ele, i) => (
                <Card
                  key={i}
                  ele={ele}
                  handlePricingCardClick={handlePricingCardClick}
                  ref={(el) => {
                    cardRefs.current[i] = el
                  }}
                  handleGlobalConsent={handleGlobalConsent}
                  globalConsent={globalConsent}
                />
              ))}
        </div>
      </div>

      <style jsx global>{`
                .custom-scrollbar::-webkit-scrollbar {
                  width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                  background: #f1f1f1;
                  border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                  background: #d1d5db;
                  border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                  background: #9ca3af;
                }
              `}</style>
    </div>
  )
}

export default PortfolioSection
